import {
  type SearchAnalyticsResult,
  SearchAnalyticsSession,
  type SearchEndReason,
  type SearchOutcome,
  type SearchRankingVariant,
} from './searchAnalytics';

export type {SearchResultMetadata} from './searchAnalytics';

type GlobalSearchFilter = 'all' | 'people' | 'teams' | 'news' | 'workstreams' | 'knowledge';
type EntryPoint = 'nav' | 'home' | 'keyboard' | 'query_param';

const outcomeForEntity = (entityType: SearchAnalyticsResult['entityType']) => {
  if (entityType === 'person') return 'profile_viewed';
  if (entityType === 'team') return 'team_viewed';
  if (entityType === 'workstream' || entityType === 'workstream_scope') {
    return 'workstream_opened';
  }
  if (entityType === 'knowledge') return 'knowledge_opened';
  return 'not_applicable';
};

/** Global-search adapter over the shared GLOBAL_SEARCH_* session mechanics. */
export class GlobalSearchAnalyticsSession extends SearchAnalyticsSession {
  private filterChangeSequence = 0;
  private filterChanged = false;
  private showMoreUsed = false;

  constructor(entryPoint: EntryPoint, rankingVariant: SearchRankingVariant) {
    super(rankingVariant);
    this.log('GLOBAL_SEARCH_SESSION_STARTED', {
      search_session_id: this.sessionId,
      surface: 'global',
      typeahead_context: 'not_applicable',
      entry_point: entryPoint,
      initial_filter: 'all',
      ranking_variant: rankingVariant,
    });
  }

  resultsViewed({
    query,
    activeFilter,
    previousFilter,
    resultCount,
    shownTopK,
    elapsedMs,
    rankingVariant,
    askRowPromoted,
  }: {
    query: string;
    activeFilter: GlobalSearchFilter;
    previousFilter: GlobalSearchFilter;
    resultCount: number;
    shownTopK: number;
    elapsedMs: number;
    rankingVariant: SearchRankingVariant;
    askRowPromoted: boolean;
  }) {
    if (activeFilter !== previousFilter) {
      this.filterChanged = true;
      this.filterChangeSequence += 1;
    }
    const requestId = this.beginRequest(resultCount, rankingVariant);
    this.log('GLOBAL_SEARCH_RESULTS_VIEWED', {
      ...this.requestProperties(requestId, activeFilter),
      previous_filter: previousFilter,
      filter_change_sequence: this.filterChangeSequence,
      ...this.requestQueryProperties(query, resultCount, shownTopK, elapsedMs),
      ask_row_promoted: askRowPromoted,
      ranking_variant: activeFilter === 'people' ? rankingVariant : 'not_applicable',
    });
    return {requestId, requestSequence: this.requestSequence};
  }

  resultsImpressed({
    requestId,
    activeFilter,
    results,
  }: {
    requestId: string;
    activeFilter: GlobalSearchFilter;
    results: SearchAnalyticsResult[];
  }) {
    for (const result of results) {
      this.log('GLOBAL_SEARCH_RESULT_IMPRESSED', {
        ...this.requestProperties(requestId, activeFilter),
        ...this.impressionProperties(result),
        ranking_variant: result.entityType === 'person' ? this.rankingVariant : 'not_applicable',
      });
    }
  }

  resultSelected({
    requestId,
    activeFilter,
    result,
    selectionMethod,
  }: {
    requestId: string;
    activeFilter: GlobalSearchFilter;
    result: SearchAnalyticsResult;
    selectionMethod: 'click' | 'keyboard';
  }) {
    this.log('GLOBAL_SEARCH_RESULT_SELECTED', {
      ...this.requestProperties(requestId, activeFilter),
      ...this.selectionProperties(result),
      selection_method: selectionMethod,
      show_more_before_selection: this.showMoreUsed,
      ranking_variant: result.entityType === 'person' ? this.rankingVariant : 'not_applicable',
    });
    this.end({activeFilter, endReason: 'selected', outcome: outcomeForEntity(result.entityType)});
  }

  showMore() {
    this.showMoreUsed = true;
  }

  markFilterChanged() {
    this.filterChanged = true;
  }

  end({
    activeFilter,
    endReason = 'dismissed',
    outcome = 'not_applicable',
  }: {
    activeFilter: GlobalSearchFilter;
    endReason?: Exclude<SearchEndReason, 'input_cleared'>;
    outcome?: Exclude<SearchOutcome, 'selection_committed'>;
  }) {
    if (!this.endOnce()) return;
    this.log('GLOBAL_SEARCH_SESSION_ENDED', {
      search_session_id: this.sessionId,
      surface: 'global',
      typeahead_context: 'not_applicable',
      ...this.terminalProperties(),
      qualified_selection: this.hadSelection,
      last_active_filter: activeFilter,
      filter_changed: this.filterChanged,
      end_reason: endReason,
      outcome,
    });
  }

  private requestProperties(requestId: string, activeFilter: GlobalSearchFilter) {
    return {
      search_session_id: this.sessionId,
      request_id: requestId,
      request_sequence: this.requestSequence,
      surface: 'global',
      active_filter: activeFilter,
      typeahead_context: 'not_applicable',
    };
  }
}
